import { createPooledDigests, derivedGroup } from "./derivedLevel.ts";
import {
  createDenseReader,
  createDigestReader,
  createWordReader,
  type TDigestSource,
} from "./digestReader.ts";
import { DIGEST_PRODUCTS, type TDigestVariable } from "./digestVariables.ts";
import { createMergedDigests, mergedPath } from "./strata.ts";
import {
  digestQuantile,
  digestQuantileRange,
  type TDigestChunk,
} from "./tdigest.ts";

import { createHiveStore, isHiveStorePath } from "@/lib/data/hiveStore.ts";
import { parseStorePath } from "@/lib/data/icechunkStore.ts";
import type { TDataSource, TSourceLevel } from "@/lib/types/GlobeTypes.ts";

/** The percentiles (0..100) the derived variables are computed at. */
export type TDigestParams = {
  percentile: number;
  low: number;
  high: number;
};

/** One cell of a level: where its digest is. */
export type TDigestCell = {
  chunk: TDigestChunk;
  /** The cell's index in the chunk. */
  cell: number;
};

/** A derived variable's value for one cell; NaN for a cell without data. */
function digestValue(
  variable: Pick<TDigestVariable, "product">,
  params: TDigestParams,
  chunk: TDigestChunk,
  cell: number
) {
  return variable.product === DIGEST_PRODUCTS.RANGE
    ? digestQuantileRange(chunk, cell, params.low / 100, params.high / 100)
    : digestQuantile(chunk, cell, params.percentile / 100);
}

/**
 * A derived variable over the cells `start .. end` of a level, as an
 * ordinary float32 field. `path` is the digest array's path in the store.
 * Chunks already decoded are not read again, whatever the percentiles.
 */
export async function digestField(
  reader: TDigestSource,
  path: string,
  variable: Pick<TDigestVariable, "product">,
  params: TDigestParams,
  start: number,
  end: number
) {
  const out = new Float32Array(Math.max(end - start, 0)).fill(NaN);
  if (out.length === 0) {
    return out;
  }
  const { chunkCells } = await reader.array(path);
  const first = Math.floor(start / chunkCells);
  const last = Math.floor((end - 1) / chunkCells);
  const chunks = await Promise.all(
    Array.from({ length: last - first + 1 }, (_, index) =>
      reader.chunk(path, first + index)
    )
  );
  for (const [index, chunk] of chunks.entries()) {
    if (!chunk) {
      continue;
    }
    const origin = (first + index) * chunkCells;
    const from = Math.max(start, origin);
    const to = Math.min(end, origin + chunk.offsets.length - 1);
    for (let cell = from; cell < to; cell++) {
      out[cell - start] = digestValue(variable, params, chunk, cell - origin);
    }
  }
  return out;
}

/**
 * The digest of one cell of a level. With `cachedOnly`, nothing is read:
 * undefined when the cell's chunk is not in memory. Null where the store
 * has no chunk.
 */
export async function digestCell(
  reader: TDigestSource,
  path: string,
  cell: number,
  cachedOnly = false
): Promise<TDigestCell | null | undefined> {
  const { chunkCells } = await reader.array(path);
  const index = Math.floor(cell / chunkCells);
  const chunk = cachedOnly
    ? reader.peek(path, index)
    : await reader.chunk(path, index);
  return chunk && { chunk, cell: cell - index * chunkCells };
}

function openReaders(storePath: string) {
  return createHiveStore(parseStorePath(storePath).url).then((store) => {
    const digests = createDigestReader(store);
    const pooled = createPooledDigests(digests);
    return {
      store,
      digests,
      pooled,
      merged: createMergedDigests(pooled),
      dense: createDenseReader(store),
      words: createWordReader(store),
    };
  });
}

export type TLeafReaders = Awaited<ReturnType<typeof openReaders>>;

let current:
  { store: string; readers: ReturnType<typeof openReaders> } | undefined;

/**
 * The chunk readers of a store, one store at a time (the decoded chunks of
 * the previous store are let go). Only a hive store serves ragged chunks.
 * The point tier reads the leaf digests and their located words through
 * the same readers a derived level fills its caches from.
 */
export function storeReaders(storePath: string) {
  if (!isHiveStorePath(storePath)) {
    return undefined;
  }
  if (current?.store !== storePath) {
    current = { store: storePath, readers: openReaders(storePath) };
  }
  return current.readers;
}

/**
 * Where the variables of a level are computed from: the group its arrays
 * are read in (the leaves' for a derived level) and the leaf cells pooled
 * into one of its cells. `path` names one array, or several to be read and
 * merged cell by cell (the strata of a `both` choice); `digests` serves
 * either.
 */
export function levelSource(
  level: Pick<TSourceLevel, "derived">,
  source: Pick<TDataSource, "store" | "dataset">
) {
  const readers = storeReaders(source.store);
  if (!readers) {
    return undefined;
  }
  const group = derivedGroup(level.derived?.refinement ?? 0);
  const dataset = level.derived?.dataset ?? source.dataset;
  const path = (array: string) => [dataset, array].filter(Boolean).join("/");
  return {
    group,
    path: (arrays: string | string[]) =>
      Array.isArray(arrays) ? mergedPath(arrays.map(path)) : path(arrays),
    digests: readers.then(({ merged }) => merged.at(group)),
    dense: readers.then(({ dense }) => dense),
  };
}
